import { IItemCacheDispatch, IItemCacheThunkAction } from '../store/actions/ItemCacheActions';
import { offlineCacheFacet } from '../facets/ConfigurationFacets';
import { readConfiguration } from './configuration/readConfiguration';

/**
 * @public
 */
export interface IEnsureGetJSONResponse {
    <TResponse>(key: string): Promise<TResponse>;
}

/**
 * @public
 */
export interface IEnsureSetJSONResponse {
    <TResponse>(key: string, response: TResponse): Promise<TResponse>;
}

/**
 * @public
 */
export interface IIsOffline {
    (): boolean;
}

/**
 * @public
 */
export interface IOfflineCache {
    ensureGetJSONResponse?: IEnsureGetJSONResponse;
    ensureSetJSONResponse?: IEnsureSetJSONResponse;
    isOffline?: IIsOffline;
    clearCache?: () => void;
}

/**
 * @public
 */
export function getJSONResponse<TResponse>(key: string): IItemCacheThunkAction<Promise<TResponse | undefined>> {
    return async (dispatch: IItemCacheDispatch) => {
        const ensureGetJSONResponse = dispatch(getEnsureJSONResponse());

        let jsonResponse: TResponse | undefined;

        if (ensureGetJSONResponse) {
            jsonResponse = await ensureGetJSONResponse<TResponse>(key);
        }

        return jsonResponse;
    };
}

function getEnsureJSONResponse(): IItemCacheThunkAction<IEnsureGetJSONResponse | undefined> {
    return (dispatch: IItemCacheDispatch) => {
        let ensureGetJSONResponse: IEnsureGetJSONResponse | undefined;

        const {
            spDataSourceConfiguration
        } = dispatch(readConfiguration({
            spDataSourceConfiguration: offlineCacheFacet
        }));

        if (spDataSourceConfiguration && spDataSourceConfiguration.ensureGetJSONResponse) {
            ensureGetJSONResponse = spDataSourceConfiguration.ensureGetJSONResponse;
        }

        return ensureGetJSONResponse;
    };
}

/**
 * @public
 */
export function setJSONResponse<TResponse>(key: string, response: TResponse): IItemCacheThunkAction<Promise<TResponse | undefined>> {
    return async (dispatch: IItemCacheDispatch) => {
        const ensureSetJSONResponse = dispatch(setEnsureJSONResponse());

        let jsonResponse: TResponse | undefined;

        if (ensureSetJSONResponse) {
            jsonResponse = await ensureSetJSONResponse<TResponse>(key, response);
        }
        return jsonResponse;
    };
}

function setEnsureJSONResponse(): IItemCacheThunkAction<IEnsureSetJSONResponse | undefined> {
    return (dispatch: IItemCacheDispatch) => {
        let ensureSetJSONResponse: IEnsureSetJSONResponse | undefined;

        const {
            spDataSourceConfiguration
        } = dispatch(readConfiguration({
            spDataSourceConfiguration: offlineCacheFacet
        }));

        if (spDataSourceConfiguration && spDataSourceConfiguration.ensureSetJSONResponse) {
            ensureSetJSONResponse = spDataSourceConfiguration.ensureSetJSONResponse;
        }

        return ensureSetJSONResponse;
    };
}

/**
 * @public
 */
export function isOffline(): IItemCacheThunkAction<boolean> {
    return (dispatch: IItemCacheDispatch) => {
        const ensureGetOfflineMode = dispatch(getEnsureOfflineMode());

        if (ensureGetOfflineMode) {
            return ensureGetOfflineMode();
        }
        return false;
    };
}

function getEnsureOfflineMode(): IItemCacheThunkAction<IIsOffline | undefined> {
    return (dispatch: IItemCacheDispatch) => {
        let ensureGetOfflineMode: (() => boolean) | undefined;

        const {
            spDataSourceConfiguration
        } = dispatch(readConfiguration({
            spDataSourceConfiguration: offlineCacheFacet
        }));

        if (spDataSourceConfiguration && spDataSourceConfiguration.isOffline) {
            ensureGetOfflineMode = spDataSourceConfiguration.isOffline;
        }

        return ensureGetOfflineMode;
    };
}

/**
 * @public
 */
export function clearCache(): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        const {
            spDataSourceConfiguration
        } = dispatch(readConfiguration({
            spDataSourceConfiguration: offlineCacheFacet
        }));

        if (spDataSourceConfiguration && spDataSourceConfiguration.clearCache) {
            spDataSourceConfiguration.clearCache();
        }
    };
}
